import { useEffect, useRef } from "react"
import { useVirtualizer } from "@tanstack/react-virtual"
import { Send, Check, CheckCheck, X } from "lucide-react"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Progress } from "@/components/ui/progress"
import { Skeleton } from "@/components/ui/skeleton"
import { cn } from "@/lib/utils"
import type { ChatMessage } from "./whatsappChat"
import { ToMarkdown } from "../ui/tomarkdown"
import { MessageItem } from "./message"


function MessageSkeleton({ outgoing }: { outgoing: boolean }) {
  return <div className={cn("flex mb-2", outgoing ? "justify-end" : "justify-start")}>
    <Skeleton className={cn("h-10 rounded-2xl", outgoing ? "w-48" : "w-56")} />
  </div>
}

interface MessageSurfaceProps {
  selectedContact: Contact | null
  messages: ChatMessage[]
  loading: boolean
  progress?: number
  emptyMessage: string
  onDeselect: () => void
  contactInfo?: React.ReactNode
  hideContactName?: boolean
  evaluationItems?: Record<string, Evaluation_Item>
}

export function MessageSurface({ evaluationItems, selectedContact, messages, loading, progress, emptyMessage, onDeselect, contactInfo, hideContactName = false }: MessageSurfaceProps) {
  const messagesListRef = useRef<HTMLDivElement>(null)

  const virtualizer = useVirtualizer({
    count: messages.length,
    getScrollElement: () => messagesListRef.current,
    estimateSize: () => 80,
    overscan: 10,
    measureElement: (el) => el.getBoundingClientRect().height,
  })

  useEffect(() => {
    if (!messagesListRef.current || messages.length === 0) return
    const el = messagesListRef.current
    const scrollToBottom = () => { el.scrollTop = el.scrollHeight }
    scrollToBottom()
    // Virtualizer measures elements asynchronously; scroll again after layout settles
    const t1 = setTimeout(scrollToBottom, 50)
    const t2 = setTimeout(scrollToBottom, 150)
    return () => { clearTimeout(t1); clearTimeout(t2) }
  }, [selectedContact?.id, messages.length])

  if (!selectedContact) return <div className="flex flex-col items-center justify-center flex-1 bg-muted/30 gap-4">
    <div className="h-20 w-20 rounded-full bg-muted flex items-center justify-center">
      <Send className="h-8 w-8 text-muted-foreground" />
    </div>
    <p className="text-muted-foreground text-sm text-center px-4">{emptyMessage}</p>
  </div>

  return <>

    {/* Chat header */}
    <div className="flex items-center gap-3 px-4 py-2 border-b border-border bg-background shrink-0">
      <Avatar className="shrink-0 h-9 w-9">
        <AvatarImage src={selectedContact.avatar} alt={selectedContact.name} />
        <AvatarFallback>{selectedContact.name?.[0]?.toUpperCase() ?? "?"}</AvatarFallback>
      </Avatar>
      {/* <div className="flex-1 min-w-0"> */}
      {!hideContactName && <div className="min-w-0">
        <p className="text-foreground text-sm font-semibold truncate">{selectedContact.name}</p>
      </div>}
      <div className="grow min-w-0">
        {contactInfo}
      </div>
      <button onClick={onDeselect} className="p-1.5 rounded-full hover:bg-muted transition-colors text-muted-foreground hover:text-foreground shrink-0" title="Deselect contact">
        <X className="h-4 w-4" />
      </button>
    </div>

    {loading && progress != null && <Progress value={progress} className="h-1 rounded-none shrink-0" />}

    {/* Messages area */}
    <div ref={messagesListRef} className="flex-1 overflow-y-auto py-3" style={{ backgroundColor: "#e5ddd5" }}>

      {loading ?
        <div className="space-y-2">
          {Array.from({ length: 10 }).map((_, i) => <MessageSkeleton key={i} outgoing={i % 3 === 0} />)}
        </div>
        : messages.length === 0 ?
          <div className="flex items-center justify-center h-full">
            <div className="bg-muted text-muted-foreground text-xs px-4 py-2 rounded-lg">No messages yet.</div>
          </div>
          :
          <div style={{ height: `${virtualizer.getTotalSize()}px`, position: "relative" }}>
            {virtualizer.getVirtualItems().map(virtualItem => {
              const msg = messages[virtualItem.index]
              return <MessageItem key={msg.id} msg={msg} measureRef={virtualizer.measureElement} virtualItem={virtualItem} evaluationItems={evaluationItems} />
            })}
          </div>
      }
    </div>
  </>
}
